import { useEffect, useMemo, useState } from 'react';

import { Alert, Button, Modal, Form, Input, Select } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import htmlToDraft from 'html-to-draftjs';
import { openNotification, showErrorMessage } from 'utils';
import draftToHtml from 'draftjs-to-html';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSendArticleOrderMeetMutation } from 'services';

export const OrderMailModal = ({ offer, open, close }) => {
  const [form] = Form.useForm();
  const { formatMessage } = useIntl();

  const [mailContent, setMailContent] = useState(EditorState.createEmpty());

  const [sendArticleOrderMeet, { isLoading }] = useSendArticleOrderMeetMutation();

  const authorOptions = useMemo(() => {
    if (offer.authors) {
      return offer.authors.map((author) => ({
        label: author.author.user?.firstName + ' ' + author.author.user?.lastName,
        value: author.author.user.author,
      }));
    }
  }, [offer]);

  // Yabancı yazara sistemden mail gitmez; backend maili sorumlu ilim heyeti başkanı ve
  // sekreterine yönlendirir. Seçenek listede kalır, gönderen sadece bilgilendirilir.
  const selectedAuthorIri = Form.useWatch('author', form);
  const isSelectedAuthorForeign = Boolean(offer.authors?.some(({ author }) => author.isForeign && author.user.author === selectedAuthorIri));

  useEffect(() => {
    const html = formatMessage(
      { id: 'article.order.sendMailModalTemplate' },
      {
        title: offer?.title,
        link: process.env.REACT_APP_PUBLIC_BASE_URL,
      }
    );
    const contentBlock = htmlToDraft(html);

    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      const editorState = EditorState.createWithContent(contentState);
      setMailContent(editorState);
    }
  }, [offer, formatMessage]);

  async function submit(values) {
    const { subject, author } = values;
    const content = draftToHtml(convertToRaw(mailContent.getCurrentContent()));

    const data = {
      articleOffer: offer?.['@id'],
      type: 2,
      author,
      subject,
      content,
    };

    try {
      await sendArticleOrderMeet(data);

      openNotification({
        message: formatMessage({ id: 'appModule.actionSuccessfully' }),
      });

      close();
    } catch (error) {
      showErrorMessage(error);
    }
  }

  return (
    <Modal
      footer={null}
      width="40%"
      open={open}
      onCancel={close}
      maskClosable={false}
      className="gx-px-4"
      centered
      destroyOnClose
    >
      <h1 className="gx-text-center">{offer?.title}</h1>
      <h2 className="gx-text-center gx-mb-4">
        <FormattedMessage id="appModule.sendMail" />
      </h2>

      <Form
        form={form}
        onFinish={submit}
        layout="vertical"
      >
        <Form.Item
          name="author"
          label="Yazar"
          className="gx-mb-3"
        >
          <Select
            allowClear
            placeholder="Yazar seçiniz"
            options={authorOptions}
          />
        </Form.Item>
        {isSelectedAuthorForeign && (
          <Alert
            className="gx-mb-3"
            description="Bu mail yazara ulaşmaz; sistem maili sorumlu ilim heyeti başkanı ve sekreterine yönlendirir."
            type="warning"
            showIcon
            icon={<InfoCircleOutlined />}
          />
        )}
        <Form.Item
          name="subject"
          label="Konu"
          className="gx-mb-3"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Editor
          toolbar={{ options: ['inline', 'link', 'remove', 'history'] }}
          onEditorStateChange={(value) => setMailContent(value)}
          editorState={mailContent}
        />
        <div className="gx-d-flex gx-mt-4">
          <Button
            onClick={close}
            block
          >
            <FormattedMessage id="appModule.cancel" />
          </Button>

          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            block
          >
            <FormattedMessage id="appModule.send" />
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
